import { i18n } from '../../i18n/i18n';
import { IDs } from '../../helpers/IDs';
import * as React from 'react';
import { SectionStyle } from '../../helpers/SectionStyle';
import { WActionButton } from '../../core/elements/WActionButton';
import { WHelpIcon } from '../../core/elements/WHelpIcon';
import { WpdfText } from '../datenauswertung/pdf/WpdfText.';
import { WpdfView } from '../datenauswertung/pdf/WpdfView';

export interface ZusatztoolsPvEingabeProps {
    data: any;
    isExpert?: boolean;
    isPdf?: boolean;
    dachausrichtung: string;
    dachflaeche: number;
    dachneigung: number;
    gesamtkosten: number;
    groesseAnlage: number;
    investitionskosten: number;
    jahrInbetriebnahme: number;
    lastprofil: string;
    monatInbetriebnahme: number;
    strompreis: number;
    strompreissteigerung: number;
    stromverbrauch: number;
    onDachausrichtungChange: (data: string) => void;
    onDachflaecheChange: (data: number) => void;
    onDachneigungChange: (data: number) => void;
    onGesamtkostenChange: (data: number) => void;
    onGroesseAnlageChange: (data: number) => void;
    onInvestitionskostenChange: (data: number) => void;
    onJahrInbetriebnahmeChange: (data: number) => void;
    onLastprofilChange: (data: string) => void;
    onMonatInbetriebnahmeChange: (data: number) => void;
    onStrompreisChange: (data: number) => void;
    onStrompreissteigerungChange: (data: number) => void;
    onStromverbrauchChange: (data: number) => void;
    onReloadStromVerbrauch: () => void;
    onReloadStromPreis: () => void;
}

export class ZusatztoolsPvEingabe extends React.Component<ZusatztoolsPvEingabeProps, {}> {

    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private formatEuro: any = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    private formatFloat: any = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: true });

    getData(): any {
        const divStyle: React.CSSProperties = { marginLeft: 5, display: 'inline-flex' };
        const styleProps: React.CSSProperties = { margin: 0 };
        const strombutton_verbrauch = <WActionButton
            tooltip='Stromverbrauch übernehmen'
            label='Stromverbrauch übernehmen'
            action='reload'
            divStyle={divStyle}
            style='small'
            styleProps={styleProps}
            onAction={() => this.props.onReloadStromVerbrauch()}
        />;
        const strombutton_preis = <WActionButton
            tooltip='Stromverbrauch übernehmen'
            label='Stromverbrauch übernehmen'
            action='reload'
            divStyle={divStyle}
            style='small'
            styleProps={styleProps}
            onAction={() => this.props.onReloadStromPreis()}
        />;

        const data: any = {
            stromverbrauch: {
                name: 'Stromverbrauch' + (!this.props.isPdf ? ' [kWh]' : ''),
                value: this.renderInput({
                    value: this.props.stromverbrauch,
                    min: 0,
                    onChange: (evt: any) => this.props.onStromverbrauchChange(evt.target.value),
                    pdf: `${this.formatFloat.format(this.props.stromverbrauch)} kWh`,
                }),
                button: strombutton_verbrauch
            },
            dachflaeche: {
                name: 'Zur Verfügung stehende Dachfläche' + (!this.props.isPdf ? ' [m²]' : ''),
                value: this.renderInput({
                    value: this.props.dachflaeche,
                    min: 1,
                    onChange: (evt: any) => this.props.onDachflaecheChange(evt.target.value),
                    pdf: `${this.formatFloat.format(this.props.dachflaeche)} m²`
                })
            },
            dachneigung: {
                name: 'Dachneigung' + (!this.props.isPdf ? ' [°]' : ''),
                value: this.renderAuswahlDachneigung()
            },
            dachausrichtung: {
                name: 'Dachausrichtung',
                value: this.renderAuswahlDachausrichtung()
            },
            lastprofil: {
                name: 'Auswahl Lastprofil',
                value: this.renderAuswahlLastprofil()
            },
            strompreis: {
                name: 'Strompreis' + (!this.props.isPdf ? ' [€]' : ''),
                value: this.renderInput({
                    value: this.props.strompreis,
                    min: 0,
                    onChange: (evt: any) => this.props.onStrompreisChange(evt.target.value),
                    pdf: `${this.formatEuro.format(this.props.strompreis)}`
                }),
                button: strombutton_preis
            },
            groesseAnlage: {
                name: 'Größe PV-Anlage' + (!this.props.isPdf ? ' [kWp]' : ''),
                value: this.renderInput({
                    value: this.props.groesseAnlage,
                    min: 1,
                    max: 100,
                    onChange: (evt: any) => this.props.onGroesseAnlageChange(evt.target.value),
                    pdf: `${this.formatFloat.format(this.props.groesseAnlage)} kWp`
                }),
                hinweis: this.getHinweisAnlagengroesse()
            },
            strompreissteigerung: {
                name: 'Strompreissteigerung' + (!this.props.isPdf ? ' [%]' : ''),
                value: this.renderInput({
                    value: this.props.strompreissteigerung,
                    min: 0,
                    onChange: (evt: any) => this.props.onStrompreissteigerungChange(evt.target.value),
                    pdf: `${this.formatFloat.format(this.props.strompreissteigerung)}%`
                })
            },
            jahrInbetriebnahme: {
                name: 'Jahr der Inbetriebnahme',
                value: this.renderAuswahlJahrInbetriebnahme(),
                info: !this.hasEinspeisungsverguetung() ? 'keine Einspeisevergütung hinterlegt' : null
            },
            monatInbetriebnahme: {
                name: 'Monat der Inbetriebnahme' + (!this.props.isPdf ? ' (1-12)' : ''),
                value: this.renderAuswahlMonatInbetriebnahme()
            },
            investitionskosten: {
                name: 'spezifische Investitionskosten (optionale Eingabe)' + (!this.props.isPdf ? ' [€/kWp]' : ''),
                value: this.renderInput({
                    value: this.props.investitionskosten,
                    min: 0,
                    onChange: (evt: any) => this.props.onInvestitionskostenChange(evt.target.value),
                    pdf: `${this.formatEuro.format(this.props.investitionskosten)}/kWp`
                })
            } /*,
            gesamtkosten: {
                name: 'Gesamtkosten der Anlage ' + (!this.props.isPdf ? ' [€]' : ''),
                value: this.renderInput({
                    value: this.props.gesamtkosten,
                    min: 0,
                    onChange: (evt: any) => this.props.onGesamtkostenChange(evt.target.value),
                    pdf: `${this.formatEuro.format(this.props.gesamtkosten)}`
                })
            }*/
        };

        if (!this.props.isExpert) {
            delete data.groesseAnlage;
            delete data.strompreissteigerung;
            delete data.jahrInbetriebnahme;
            delete data.monatInbetriebnahme;
            delete data.investitionskosten;
            delete data.gesamtkosten;
        }

        return data;
    }

    getHinweisAnlagengroesse(): string {
        const groesse: number = this.props.groesseAnlage || 0;

        return groesse > 0 && groesse < 5
            ? 'Bei "Variante 3: Maximale Rendite" wird eine minimale Anlagengröße von 5 kWp angenommen. Bei der Auswahl und Simulation von kleineren Anlagengrößen, kann es daher bei "Variante 1: Ausgewählte PV-Leistung" zu einer höheren Rendite als bei "Variante 3: Maximale Rendite" kommen.'
            : null;
    }

    getStyle(): any {
        const isPdf: boolean = this.props.isPdf || false;
        const Section: any = new SectionStyle();

        return ({
            tabelle: {
                fontSize: '9pt',
                maxWidth: 720,
                marginTop: 15,
                border: '1pt solid white',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box'
            },
            row: {
                color: '#555',
                backgroundColor: '#ddd',
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                boxSizing: 'border-box'
            },
            row_header: {
                ...(isPdf && { fontFamily: 'Helvetica-Bold' }),
                fontSize: '10pt',
                fontWeight: 'bold',
                color: Section.getPropertyValue('--color-button-text') || '#555',
                backgroundColor: Section.getPropertyValue('--color-section-main') || '#bccae3'
            },
            col1: {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                flexBasis: isPdf ? '47.5%' : '50%'
            },
            col1_header: {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                flexBasis: isPdf ? '100%' : '75%'
            },
            col2: {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                flexBasis: isPdf ? '52.5%' : '25%'
            },
            col2_header: {
                backgroundColor: 'white',
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                flexBasis: isPdf ? 0 : '25%'
            },
            col3: {
                backgroundColor: 'white',
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                flexBasis: isPdf ? 0 : '25%'
            },
            cell: {
                textAlign: 'left',
                padding: isPdf ? 2 : 4,
                borderTop: '1pt solid white',
                borderLeft: '1pt solid white',
                display: 'flex',
                flex: 1,
                justifyContent: 'flex-start',
                ...(!isPdf && { alignItems: 'center' }),
                boxSizing: 'border-box'
            },
            cell_header: {
                borderTop: 0
            },
            cell1: {
                borderLeft: 0
            },
            cell1_header: {
                borderLeft: 0
            },
            cell2: {
                ...(!isPdf && { padding: 0 })
            },
            cell3: {
                padding: isPdf ? '1px 2px' : '2px 4px'
            },
            cell3h: {
                backgroundColor: '#fee',
                position: 'relative'
            },
            input: {
                fontSize: '9pt',
                color: 'inherit',
                backgroundColor: '#fff',
                borderRadius: '5px',
                margin: '2px',
                width: '100%',
                maxHeight: 'var(--formfields-compact-height)',
                minHeight: 'var(--formfields-compact-height)',
                borderColor: 'transparent'
            },
            select: {
                fontSize: '9pt',
                color: 'inherit',
                /*backgroundColor: 'inherit',*/
                backgroundColor: '#fff',
                borderRadius: '5px',
                margin: '2px',
                width: '100%',
                maxHeight: 'var(--formfields-compact-height)',
                minHeight: 'var(--formfields-compact-height)',
                borderColor: 'transparent',
                cursor: 'pointer'
            },
            button: {
                marginTop: -2,
                marginBottom: -2,
                width: 180,
                display: 'flex',
                justifyContent: 'right'
            },
            hinweis: {
                fontSize: '8pt',
                color: '#a77',
                backgroundColor: 'white',
                maxHeight: '1em'
            },
            info: {
                color: '#a77',
                backgroundColor: '#fee'
            }
        });
    }

    hasEinspeisungsverguetung(): boolean {
        const data: any = this.props.data || {};
        const jahr: number = +this.props.jahrInbetriebnahme || null;
        // console.log('EEG', data[IDs.ENT_EEG_VERGUETUNG] );
        return (data[IDs.ENT_EEG_VERGUETUNG] || [])
            .some((element: any) => jahr && element.jahr === jahr);
        // return (data[IDs.ENT_ENERGIEDATEN_EIGENSTROM] || [])
        //     .some((element: any) => jahr && (element.jahr || null) === jahr);
    }

    render(): JSX.Element {
        return this.props.data
            ? <>{this.renderTabelle()}</>
            : null;
    }

    renderAuswahlDachausrichtung(): JSX.Element {
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();
        const values: any = {
            sued: 'Süd',
            ost: 'Ost',
            west: 'West',
            ost_west: 'Ost+West'
        };

        return !isPdf
            ? <select
                style={styles.select}
                value={this.props.dachausrichtung}
                onChange={(evt: any) => this.props.onDachausrichtungChange(evt.target.value)}
            >
                <option key='dachausrichtung-' value=''>{`<${i18n('keine Auswahl', 'sys')}>`}</option>
                {Object.keys(values).map((key: any, index: number) =>
                    <option key={`dachausrichtung-${index}`} value={key}>{values[key] || null}</option>
                )}
            </select>
            : <WpdfText pdf={isPdf}>
                {this.props.dachausrichtung ? values[this.props.dachausrichtung] || null : null}
            </WpdfText>;
    }

    renderAuswahlDachneigung(): JSX.Element {
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();
        const values: any = {
            0: '0°',
            30: '30°',
            15: '15°',
            45: '45°',
            90: '90°'
        };

        return !isPdf
            ? <select
                style={styles.select}
                value={this.props.dachneigung}
                onChange={(evt: any) => this.props.onDachneigungChange(evt.target.value)}
            >
                <option key='dachneigung-' value=''>{`<${i18n('keine Auswahl', 'sys')}>`}</option>
                {Object.keys(values).map((key: any, index: number) =>
                    !(this.props.dachausrichtung === 'ost_west' && (['0', '90'].indexOf(key) !== -1))
                        ? <option key={`dachneigung-${index}`} value={key}>
                            {values[key] || null}
                        </option>
                        : null
                )}
            </select>
            : <WpdfText pdf={isPdf}>
                {this.props.dachneigung ? values[this.props.dachneigung] || null : null}
            </WpdfText>;
    }

    renderAuswahlLastprofil(): JSX.Element {
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();

        return !isPdf
            ? <select
                style={styles.select}
                value={this.props.lastprofil}
                onChange={(evt: any) => this.props.onLastprofilChange(evt.target.value)}
            >
                <option value=''>{`<${i18n('keine Auswahl', 'sys')}>`}</option>
                <option value='g0'>G0 - Gewerbe allgemein</option>
                <option value='g1'>G1 - werktags 8-18 Uhr</option>
                <option value='g2'>G2 - überwiegend Abendstunden</option>
                <option value='g3'>G3 - durchlaufend (mit Kühlbedarf)</option>
                <option value='g4'>G4 - klassische "Ladenöffnungszeiten"</option>
                <option value='g5'>G5 - Frühmorgens/Nachts (Backstuben)</option>
                <option value='g6'>G6 - Landwirtschaftsbetriebe </option>
            </select>
            : <WpdfText pdf={isPdf}>{this.props.lastprofil ? this.props.lastprofil.toUpperCase() : null}</WpdfText>;
    }

    renderAuswahlJahrInbetriebnahme(): JSX.Element {
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();
        const options: JSX.Element[] = [];
        const startjahr: number = ((this.props.data[IDs.ENT_KONFIGURATION] || [])
            .find((item: any) => item.startjahr_datenerfassung) || {}).startjahr_datenerfassung || null;
        const jahrMax: number = new Date().getFullYear() + 1;
        let jahr: number = +startjahr > 1900 ? startjahr : 2010;

        for (; jahr <= jahrMax; ++jahr) {
            options.push(
                <option key={`jahr_inbetriebnahme-${jahr}`} value={jahr}>{jahr}</option>
            );
        }

        return !isPdf
            ? <select
                style={styles.select}
                value={this.props.jahrInbetriebnahme}
                onChange={(evt: any) => this.props.onJahrInbetriebnahmeChange(evt.target.value)}
            >
                {options}
            </select>
            : <WpdfText pdf={isPdf}>{this.props.jahrInbetriebnahme || null}</WpdfText>;
    }

    renderAuswahlMonatInbetriebnahme(): JSX.Element {
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();
        const options: JSX.Element[] = [];

        for (let monat = 1; monat <= 12; ++monat) {
            options.push(
                <option key={`monat_inbetriebnahme-${monat}`} value={monat}>{monat}</option>
            );
        }

        return !isPdf
            ? <select
                style={styles.select}
                value={this.props.monatInbetriebnahme}
                onChange={(evt: any) => this.props.onMonatInbetriebnahmeChange(evt.target.value)}
            >
                {options}
            </select>
            : <WpdfText pdf={isPdf}>{this.props.monatInbetriebnahme || null}</WpdfText>;
    }

    renderEingaben(data: any): JSX.Element[] {
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();

        return Object.keys(data || {}).map((key: any) => {
            const eintrag: any = data[key] || {};
            return (
                <WpdfView key={`eingabe-${key}`} style={{...styles.row}} pdf={isPdf}>
                    <WpdfView style={styles.col1} pdf={isPdf}>
                        <WpdfView style={{...styles.cell, ...styles.cell1}} pdf={isPdf}>
                            {!isPdf &&
                                this.renderHelpIcon(`pvtool_eingabe_${key}`, eintrag.name || null)
                            }
                            <WpdfText style={{ minWidth: 148 }} pdf={isPdf}>{eintrag.name || null}</WpdfText>
                            {(!isPdf && eintrag.button) &&
                                <div style={styles.button}>
                                    {eintrag.button}
                                </div>
                            }
                        </WpdfView>
                    </WpdfView>
                    <WpdfView style={styles.col2} pdf={isPdf}>
                        <WpdfView style={{...styles.cell, ...styles.cell2}} pdf={isPdf}>
                            {eintrag.value || null}
                        </WpdfView>
                    </WpdfView>
                    {!isPdf &&
                        <WpdfView
                            style={{...styles.col3, ...(eintrag.info && styles.info), ...(eintrag.hinweis && styles.hinweis)}} pdf={isPdf}
                        >
                            <WpdfView
                                style={{...styles.cell, ...styles.cell3, ...(eintrag.hinweis && styles.cell3h)}}
                                pdf={isPdf}
                            >
                                {!isPdf && eintrag.info &&
                                    <WpdfText pdf={isPdf}>{eintrag.info || null}</WpdfText>
                                }
                                {!isPdf && eintrag.hinweis &&
                                    <WpdfText pdf={isPdf}>{eintrag.hinweis || null}</WpdfText>
                                }
                            </WpdfView>
                        </WpdfView>
                    }
                </WpdfView>
            );
        });
    }

    renderHelpIcon(key: string, label: string): JSX.Element {
        return (
            <WHelpIcon
                data={this.props.data}
                styleDivProps={{ marginRight: 4, display: 'flex', alignSelf: 'center' }}
                styleProps={{ margin: 0 }}
                label={label || 'Hilfe'}
                helpKey={key || null}
                type='theme_info'
                style='theme_small'
                editBar
                useTheme
            />
        );
    }

    renderInput(props: any): JSX.Element {
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();

        return !isPdf
            ? <input
                style={styles.input}
                type='number'
                min={typeof props.min !== 'undefined' ? props.min : null}
                max={typeof props.max !== 'undefined' ? props.max : null}
                step='any'
                value={typeof props.value !== 'undefined' ? props.value : ''}
                onChange={props.onChange || null}
            />
            : <WpdfText pdf={isPdf}>{props.value ? props.pdf || this.formatFloat.format(props.value) : null}</WpdfText>;
    }

    renderTabelle(): JSX.Element {
        const data: any = this.getData() || {};
        const isPdf: boolean = this.props.isPdf || false;
        const styles: any = this.getStyle();

        return (
            <WpdfView style={styles.tabelle} pdf={isPdf}>
                <WpdfView style={{...styles.row, ...styles.row_header}} pdf={isPdf}>
                    <WpdfView style={{...styles.col1_header}} pdf={isPdf}>
                        <WpdfView style={{...styles.cell, ...styles.cell_header, ...styles.cell1_header}} pdf={isPdf}>
                            <WpdfText pdf={isPdf}>Eingabe</WpdfText>
                        </WpdfView>
                    </WpdfView>
                    {!isPdf &&
                        <WpdfView style={{...styles.col2_header}} pdf={isPdf}>
                            <WpdfView style={{...styles.cell, ...styles.cell_header, ...styles.cell2_header}} pdf={isPdf} />
                        </WpdfView>
                    }
                </WpdfView>
                {this.renderEingaben(data)}
            </WpdfView>
        );
    }
}
